import moment from 'moment';
import * as React from 'react';
import { Redirect } from 'react-router-dom';
import {
  OptionProps,
  OptionsType,
  OptionTypeBase,
  ValueType,
} from 'react-select/src/types';
import { StylesConfig } from 'react-select/src/styles';
import { SensorWithMetrics } from './routes/dashboard/SensorDetailsView';
import {
  CredentialField,
  InputState,
  OBSensorConfigurations,
  OBSensorDeploymentCapabilities,
  OBServiceTypes,
  OptionType,
  PermissionEnum,
  Scaled,
  SensorEntry,
  ServicesEnum,
  SessionStorageKey,
  UserRoleEnum,
} from './types';
import FileSaver from 'file-saver';
import COUNTRIES from './countries.json';
import styled from 'styled-components';
import { SelectOptions } from './widgets/FormikInputField';

export const AWN_USERINFO_REQUEST_HEADER = 'X-AW-Userinfo';
export const AWN_USERINFO_POST_FIELD = 'http_x_aw_userinfo';
export const AWN_CUSTOMER_HEADER = 'x-awn-cust';
export const PRIMARY_JWT_COOKIE_NAME = 'portal_primary_jwt';
export const SECONDARY_JWT_COOKIE_NAME = 'portal_secondary_jwt';
export const AUTH_ERROR_COOKIE_NAME = 'auth_error';

export const LOCAL_DATE_FORMAT = 'YYYY-MM-DD';
export const LOCAL_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm';
export const UTC_DATETIME_FORMAT = 'YYYY-MM-DD[T]HH:mm[Z]';
export const UTC_DATETIME_SECONDS_FORMAT = 'YYYY-MM-DD[T]HH:mm:ss[Z]';
export const TOOLTIP_DATETIME_FORMAT = 'YYYY-MM-DD [at] HH:mm';
export const INCIDENT_SCORE_DATE_FORMAT = 'MMM Do YYYY';
export const INPUT_DATE_FORMATS = [moment.ISO_8601, LOCAL_DATETIME_FORMAT];
export const INPUT_TIME_FORMATS = [moment.HTML5_FMT.TIME_MS, 'h:mma'];

export const DEFAULT_SECURITY_SCORE = 'N/A';
export const MISSING_VALUE_PLACEHOLDER = '---';
export const PLACEHOLDER_START = '\u2907';
export const PLACEHOLDER_END = '\u2906';
export const PLACEHOLDER_DEFAULT_DIVIDER = '\u2904';

export const LOG_SEARCH_RESULT_PAGE_SIZE = 500;
export const LOG_SEARCH_MAX_EXPORT_SIZE = 1000000;
export const AGENT_ENDPOINT_PAGE_SIZE = 100;

const SIZES = ['', 'K', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];

export const ROUTE_PATH = {
  CONTACT: '/contact',
  CONTACT_ONBOARDING: '/contact/onboarding',
  CONTACT_CST: '/contact/cst',
  SELF_SERVICE: {
    MAIN: '/connected-accounts',
    CONNECTED_ACCOUNTS: {
      DEPRECATED: '/credentials', // for backcompat
      MAIN: '/connected-accounts',
      NEW: '/connected-accounts/new',
      EDIT: '/connected-accounts/edit',
    },
    VIRTUAL: '/dashboard/sensors/virtual',
  },
  CUSTOMER_SWITCH: '/customers',
  AWS_TEMPLATE: '/awstemplate',
  LOGIN: '/login',
  DASHBOARD: {
    MAIN: '/dashboard',
    SECURITY_SCORE: '/dashboard/security-score',
    COVERAGE_SCORE: '/dashboard/coverage-score',
    SENSORS: '/dashboard/sensors',
    ENDPOINT: '/dashboard/endpoints',
    STATUS: '/dashboard/status',
    RISK: 'https://risk.arcticwolf.com',
    UNIFIED: 'https://dashboard.arcticwolf.com',
    AWARENESS: 'https://sat.arcticwolf.com/Home/Dashboard',
    TETRA: 'https://cyberjumpstart.arcticwolf.com/awn-organization',
  },
  DATA: {
    MAIN: '/data',
    FLOW: '/data/flow',
    AD: '/data/ad',
  },
  INSTALLER: '/installer',
  DOWNLOADS: {
    INTERNAL: '/downloads',
    UNIFIED_PORTAL:
      'https://dashboard.arcticwolf.com/resource-center/downloads',
  },
  LOG_SEARCH: {
    MAIN: '/logsearch',
    ACTIVITY: '/logsearch/activity',
    LOG_SOURCES: '/logsearch/sources',
  },
  MSP_DASHBOARD: '/msp',
  ROOT: '/',
  SHARE: '/contact/share',
  TICKETS: '/tickets',
  ONBOARDING: {
    MAIN: '/onboarding',
    CONTACTS: {
      MAIN: '/onboarding/contacts',
      NEW: '/onboarding/contacts/new',
      EDIT: '/onboarding/contacts/edit',
    },
    SENSORS: {
      MAIN: '/onboarding/sensors',
      NEW: '/onboarding/sensors/new',
      EDIT: '/onboarding/sensors/edit',
    },
    SCANNERS: {
      MAIN: '/onboarding/scanners',
      NEW: '/onboarding/scanners/new',
      EDIT: '/onboarding/scanners/edit',
    },
  },
  REPORTS: '/reports',
  WHITELISTABLE_IPS: '/whitelistable-ips', // deprecated, will be redirected to the one below
  SERVICE_ENDPOINTS: '/service-endpoints',
  STATUS: '/status',
  DEVICE_CONFIGURE: '/device/configure',
};
export const DEFAULT_ROUTE_PATH = ROUTE_PATH.ONBOARDING.MAIN;

// For log search id, matches uuid in 8-4-4-4-12 format
export const UUID_REGEX =
  '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

// For global app constants and utils
export const API = {
  credentials: '/api/v1/credentials',
  credentialSchema: '/api/v1/credential-schema',
  customerSwitch: '/api/v1/customer-switch',
  flowData: '/api/v1/flows',
  installer: '/api/v1/generate-installer',
  agents: '/api/v1/agents',
  availableAgentPlatforms: 'api/v1/agent/available-platforms',
  downloadAgent: '/api/v1/agent/download',
  login: '/api/v1/auth/login',
  auth0: '/api/v1/authenticate',
  logout: '/api/v1/logout',
  logParserTag: '/api/v1/log-system-tags',
  logSearch: '/api/v1/logsearch',
  logSearchEarliestRecord: '/api/v1/logsearch-earliest',
  logSearchResults: '/api/v1/logsearch-results',
  logSources: '/api/v1/log-sources',
  logSourceTag: '/api/v1/log-source-tag',
  metricsCountryFlows: '/api/v1/metrics/country-flows',
  metricsIncidents: (orgId: string, pod: string) =>
    `https://rendall.${pod}-prod.arcticwolf.net/api/v1/organizations/${orgId}/incidents`,
  metricsSensors: '/api/v1/metrics/sensors',
  metricsSummary: '/api/v1/metrics/summary',
  mspEndCustomers: '/api/v1/msp-customers',
  onboarding: {
    external: '/api/v1/onboarding/external',
    sites: '/api/v1/onboarding/sites',
    sitesV2: {
      base: '/api/v2/onboarding/sites',
      markComplete: '/api/v2/onboarding/sites/status/complete',
      markIncomplete: '/api/v2/onboarding/sites/status/incomplete',
      prpBase: (customerId: string, siteId: string) =>
        `/api/onboarding/v2/${customerId}/sites/${siteId}/`,
    },
    contactsV2: {
      base: '/api/v2/onboarding/contacts',
      markComplete: '/api/v2/onboarding/contacts/status/complete',
      markIncomplete: '/api/v2/onboarding/contacts/status/incomplete',
    },
    sensors: '/api/v1/onboarding/sensors',
    scanners: '/api/v1/onboarding/scanners',
    cloudSensors: '/api/v1/onboarding/cloud',
    virtualScanner: '/api/v1/onboarding/virtual',
    completeDataEntry: '/api/v1/onboarding/complete-data-entry',
    hardwareModels: '/api/v1/onboarding/hardware-models',
    allotment: '/api/v1/onboarding/allotment',
    doSync: '/api/v1/onboarding/sync',
    onboardingTeamContacts: '/api/v1/onboarding/onboarding-team-contacts',
    products: '/api/v1/onboarding/products',
    availableHardware: '/api/v1/onboarding/available-hardware',
    endpoint: '/api/v1/onboarding/endpoint',
    virtualSensor: '/api/v1/onboarding/virtual-sensor',
    availableEndpoints: '/api/v1/onboarding/available-endpoints',
    availableVirtualSensors: '/api/v1/onboarding/available-virtual-sensors',
  },
  ticketAttachment: '/api/v1/ticket-attachment',
  tickets: '/api/v1/tickets',
  ticketsWaiting: '/api/v1/tickets-waiting',
  topCountries: 'api/v1/top-countries',
  userInfo: '/api/v1/userinfo',
  incidentNotification: '/api/v1/incident-notification',
  virtualLogCollectors: '/api/v1/virtual-sensors',
  virtualSensors: '/api/v1/licensing',
  provisionSensor: '/api/v1/provisioning',
  siteSensorProfiles: '/api/v1/site-sensor-profiles',
};

export const APIV2 = {
  data: {
    flow: '/api/v2/data/flows',
    ad: '/api/v2/data/adevents',
  },
  reports: {
    get: '/api/v2/reports',
    download: '/api/v2/report/download',
  },
  logSearch: '/api/v2/log-search',
};

export const PRP_API_V2 = {
  ips: (pod: string) =>
    `https://rendall.${pod}-prod.arcticwolf.net/api/v1/service-endpoints`,
  userPod: '/api/v2/userinfo/pod',
};

// generic-series is prefixed for PRP's reverse proxy
export const GENERIC_SERIES_API = {
  availableSeries: '/api/generic-series/v1/available-series/',
  capabilities: (seriesId: string) =>
    `/api/generic-series/v1/capability-schema/${seriesId}`,
  models: (seriesId: string) => `/api/generic-series/v1/models/${seriesId}`,
};

// subscriptions is prefixed for PRP's reverse proxy
export const SUBSCRIPTIONS_API = {
  currentSubscriptions: (customerId: string | null) =>
    `/api/subscriptions/v1/organizations/${customerId}/subscriptions/`,
};

export const SALESFORCE_API = {
  subscriptions: (customerId: string | null) =>
    `/api/salesforce/v1/subscriptions/${customerId}/`,
};

export const STATIC_SRC = '/static/';

export const HTTP_STATUS = {
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  AUTHENTICATION_FAIL: 401,
  PERMISSION_DENIED: 403,
  NOT_FOUND: 404,
  SERVER_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
};

export const INITIAL_INPUT_STATE = { value: '', isValid: true };
export const START_TIME_INPUT_STATE = { value: '00:00', isValid: true };
export const END_TIME_INPUT_STATE = { value: '23:59', isValid: true };
export const GET_VALID_TIME_INPUT_STATE = (timeMoment: moment.Moment) => ({
  value: timeMoment.format('HH:mm'),
  isValid: true,
});

/**
 * Navigates to the supplied external URL and fetches data from server
 * @param url - the URL to navigate to
 */
export const AssignWindowLocation = (url: string) => {
  window.location.assign(url);
};

/**
 * Navigates to the supplied external URL without history
 * @param url - the URL to navigate to
 */
export const ReplaceWindowLocation = (url: string) => {
  window.location.replace(url);
};

/**
 * Opens a new browser tab for the given URL
 */
export const OpenNewWindow = (url: string) => {
  window.open(url, '_blank');
};

/**
 * Forces the browser to reload the current page/tab
 */
export const ReloadWindow = () => {
  window.location.reload();
};

/**
 * Wrapped router redirect component that sends the view to the "default" if there
 * are insufficient permissions or an invalid route
 */
export const RedirectToDefault = () => <Redirect to={ROUTE_PATH.ROOT} />;
export const RedirectToDashboard = () => (
  <Redirect to={ROUTE_PATH.DASHBOARD.MAIN} />
);
export const RedirectToOnboarding = () => (
  <Redirect to={ROUTE_PATH.ONBOARDING.MAIN} />
);
export const RedirectToLogsearch = () => (
  <Redirect to={ROUTE_PATH.LOG_SEARCH.MAIN} />
);

export const RedirectToLogin = () => <Redirect to={ROUTE_PATH.LOGIN} />;
export const RedirectToMSP = () => <Redirect to={ROUTE_PATH.MSP_DASHBOARD} />;
export const RedirectToCustomerSwitch = () => (
  <Redirect to={ROUTE_PATH.CUSTOMER_SWITCH} />
);

/**
 * A blank component.  Useful for conditionally hiding (rendering nothing)
 */
export const Blank = (props: any) => null;

/**
 * An empty no-op function.  Useful as a default or when conditionally assigning a handler function.
 */
export const noOp = () => {};

/**
 * Gets a value from a deeply-nested object, returning the default value if anything along the way is null/undefined.
 * e.g. where house = { "roof": { "ground": { "basement" : "Made it!" }}}
 * getObjVal(house, 'roof.ground.basement')  yields "Made it!"
 * getObjVal(house, 'roof.ground.garage', "Nope") yields "Nope"
 * @param obj {Object} - the object to fetch value from
 * @param path {string} - dot-separated path to value within object
 * @param def - default value to return if any part of the path does not exist
 * @return value at the specified object path depth
 */
export const getObjVal = (obj: object | null, path: string, def: any): any => {
  const props: Array<string> = path.split('.');
  return props.reduce((nestedLevel: object, curPathFrag: string): any => {
    if (nestedLevel && nestedLevel.hasOwnProperty(curPathFrag)) {
      // property key actually exists
      const val: object = nestedLevel[curPathFrag as keyof typeof nestedLevel];
      if (val || val === 0 || val === false) {
        // return truthy values, or explicit '0' or 'false'
        return val;
      }
    }
    return def;
  }, obj || {});
};

/**
 * Returns the union of distinct values contained in two string arrays.
 * @param arr1 {string[]} - first array
 * @param arr2 {string[]} - second array
 * @return {string[]} - concatenation of arrays with any duplicates removed
 */
export const unionStringArrays = (
  arr1: Array<string> | null,
  arr2: Array<string> | null,
): Array<string> => {
  const union = (arr1 || []).concat(arr2 || []);
  return union.filter((element, index) => union.indexOf(element) === index);
};

/**
 * Filters and sorts the provided URLSearchParams object according to the provided
 * array of keys, such that the returned URLSearchParams has only the keys specified
 * in the array (if they exist) and in the order specified.  Will return an empty
 * URLSearchParams if provided null or empty input, or input where no keys match array.
 * @param filters - the input URLSearchParams object
 * @param {Array<string>} keys - array of keys in desired sort order
 * @return {URLSearchParams} limited to the supplied keys and in the supplied order
 */
export const sortURLSearchParams = (
  filters: URLSearchParams,
  keys: Array<string>,
): URLSearchParams => {
  const oldFilters = filters || new URLSearchParams();
  const newFilters = new URLSearchParams();
  keys.forEach((key) => {
    const val = oldFilters.get(key);
    if (val) {
      newFilters.set(key, val);
    }
  });
  return newFilters;
};

/**
 * Helper to get the 'origin' (everything in URL before the first / path param), handling the case
 * that the browser doesn't support the window.location.origin property.
 * @return {string} - protocol//hostname:port formatted portion of the current browser URL
 */
export const getOrigin = (): string => {
  return (
    window.location.origin ||
    window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '')
  );
};

/**
 * Helper function for merging 'className' values in a component.  Takes an array of values and
 * filters out any falsey ones, producing a final properly-spaced string of CSS class names.
 * This allows for doing <myWidget className={mergeClasses['class-one', 'other', this.props.className]} />
 * without having to deal with unspecified/null/default for this.props.className.
 * @param {[string]} classes - array of class names which could be falsey
 * @return {string} merged and trimmed string of all class names
 */
export const mergeClasses = (
  classes: Array<string | null | undefined>,
): string => {
  return classes
    .map((c) => (c ? c.trim() : ''))
    .filter((c) => c)
    .join(' ');
};

/**
 * Merges two arrays of objects into a single array of objects, by merging the individual
 * objects based on matching by the supplied key.  If an object with the key only appears
 * in one of the arrays, it will appear unmerged in the output.  If an object with the
 * key appears more than once in either array, only the first matching object from each
 * array will be merged together into the output.
 *
 * @param {string} key - the object property name to use to merge objects
 * @param {Array} array1 - array of objects
 * @param {Array} array2 - array of objects
 * @return {Array} - new array of objects where the 'key' property of each object is
 * unique and is the combination of the first matching object from each input array
 */
export const mergeObjectArrays = (
  key: string,
  array1: ReadonlyArray<{
    [key: string]: any;
  }>,
  array2: ReadonlyArray<{
    [key: string]: any;
  }>,
): Array<{
  [key: string]: any;
}> => {
  const keys = unionStringArrays(
    array1.map((el) => el[key]),
    array2.map((el) => el[key]),
  );
  return keys.map((keyVal) => ({
    ...array1.find((el) => el[key] === keyVal),
    ...array2.find((el) => el[key] === keyVal),
  }));
};

/**
 * Converts a comma-separated list of values into a subset OptionsType (array of OptionType) of
 * the supplied OptionsType for use in react-select component (invalid choices are ignored)
 * @param {?string} value - the input value string, ("" used if falsey)
 * @param {OptionsType} options - available options
 * @return {OptionsType} selected options from those available
 */
export const stringToOptions = (
  value: string | null,
  options: OptionsType<OptionType>,
): OptionsType<OptionType> => {
  const val = value || '';
  return val
    .split(',')
    .map((val) => options.find((opt) => opt.value === val))
    .filter(notEmpty);
};

/**
 * Whether or not the supplied moment is older than the specified timeout
 * @param {moment | null} lastFetched - time data was last fetched
 * @param {number} minuteTimeout - how many minutes ago is acceptable to reuse
 * @return {boolean} - true if 'lastFetched' is null/undefined or a moment older than 'minuteTimeout' ago
 */
export const isExpired = (
  lastFetched: moment.Moment | null,
  minuteTimeout: number,
): boolean => {
  if (!lastFetched) {
    return true;
  }
  return moment().subtract(minuteTimeout, 'minutes').isAfter(lastFetched);
};

/**
 * Returns a local state update object with 'startTime' and 'endTime' InputState keys as appropriate
 * to initialize time if appropriate based on changes to start/end dates and the current ("prior")
 * textual time input.
 * If the 'end' moment is set to today, the endTime will be initialized to the current time.  Otherwise
 * it will be initialized to the end of the day.
 * @param start
 * @param priorStartState
 * @param end
 * @param priorEndState
 * @return {{startTime?: InputState, endTime?: InputState}}
 */
export const initializeTimes = (
  start: moment.Moment | null,
  priorStartState: InputState,
  end: moment.Moment | null,
  priorEndState: InputState,
): { startTime?: InputState; endTime?: InputState } => {
  let update: { startTime?: InputState; endTime?: InputState } = {};
  if (start && priorStartState.value === '') {
    update.startTime = START_TIME_INPUT_STATE;
  }
  if (end && priorEndState.value === '') {
    if (moment().isSame(end, 'day')) {
      update.endTime = GET_VALID_TIME_INPUT_STATE(moment());
    } else {
      update.endTime = END_TIME_INPUT_STATE;
    }
  }
  return update;
};

/**
 * Validates the input time string in "hh:mm" format (allows AM/PM) and returns a moment with
 * the time set to the input value if it is valid; otherwise returns null.
 * The date portion of a valid moment has undefined behaviour.
 * @param {string} timeString - the input time to validate
 * @return {[moment]} returns a moment if string is valid time; otherwise undefined
 */
export const getValidTime = (timeString: string): moment.Moment | null => {
  const value = moment(timeString, INPUT_TIME_FORMATS);
  if (value.isValid()) {
    return value;
  }
  return null;
};

/**
 * Validates the input date string in YYYY-MM-DD format and returns a moment with
 * the date set to the input value if it is valid; otherwise returns undefined.
 * The time portion of valid moment is unset/default.
 * @param {string} dateString - the input date to validate
 * @return {[moment]} returns a local moment if string is valid date; otherwise undefined
 */
export const getValidDate = (dateString: string): moment.Moment | null => {
  const value = moment(
    dateString,
    ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', ...INPUT_DATE_FORMATS],
    true,
  );
  if (value.isValid()) {
    return value.local();
  }
  return null;
};

/**
 * Returns an object with 'start' and 'end' keys containing Moments of the supplied
 * timestamps, with the earliest being start and later being end
 * @param {number} timestamp1 - first ms-since-epoch timestamp
 * @param {number} timestamp2 - second ms-since-epoch timestamp
 * @return {{start, end}}
 */
export const getStartEndMoments = (
  timestamp1: number,
  timestamp2: number,
): { start: moment.Moment; end: moment.Moment } => ({
  start: moment.utc(Math.min(timestamp1, timestamp2)),
  end: moment.utc(Math.max(timestamp1, timestamp2)),
});

/***
 * Copies the date portion from the supplied source moment into the destination moment,
 * leaving the time in the destination intact.
 * If the source is invalid, the destination is untouched, otherwise the destination
 * is mutated in place by calling .set()
 * @param {moment} src - the source moment
 * @param {moment} dest - the destination moment to have date set
 * @return the original (potentially mutated) destination
 */
export const copyDate = (src: moment.Moment, dest: moment.Moment) => {
  if (moment.isMoment(src)) {
    dest.set({
      year: src.year(),
      month: src.month(),
      date: src.date(),
    });
  }
  return dest;
};

const vSensorTypes = ['vlc', 'vSensor'];
const naCloudTypes = [...vSensorTypes, 'physical', 'scanner'];
/**
 * Set of Array<SensorEntry|SensorWithMetrics> filter functions that can be used to
 * limit the list of all of a customer's sensors to those in different categories
 */
export const sensorFilter = {
  isPhysical: (sensor: SensorEntry | SensorWithMetrics) =>
    sensor.type === 'physical' && sensor.configuration !== 'scanner',
  isVlc: (sensor: SensorEntry | SensorWithMetrics) =>
    vSensorTypes.includes(sensor.type) && sensor.configuration !== 'scanner',
  isScanner: (sensor: SensorEntry | SensorWithMetrics) =>
    sensor.type === 'scanner' ||
    ((sensor.type === 'physical' || vSensorTypes.includes(sensor.type)) &&
      sensor.configuration === 'scanner'),
  isCloud: (sensor: SensorEntry | SensorWithMetrics) =>
    !naCloudTypes.includes(sensor.type),
  hasHealth: (sensor: SensorEntry | SensorWithMetrics) =>
    sensor.type !== 'connectwise',
};

/**
 * Reads the supplied File as text into a string
 * @param {File} file - the HTML5 file object to read
 * @return {Promise} a promise that resolves to the file contents or rejects with an Error
 */
export const readTextFile = (file: File): Promise<string> => {
  const fileReader = new FileReader();
  return new Promise((resolve, reject) => {
    fileReader.onerror = () => {
      fileReader.abort();
      reject(new Error('Unable to read input file.'));
    };
    fileReader.onload = (fileEvent) => {
      //@ts-ignore: TS bug - can't properly infer currentTarget
      // See https://github.com/microsoft/TypeScript/issues/299
      resolve(fileEvent.currentTarget.result);
    };
    fileReader.readAsText(file);
  });
};

/**
 * Temporary styling for react-select. As of 2.0 react-select switched to CSS-in-JS styling
 * using 'emotion'. It says it does facilitate styling using CSS classes as a backup,
 * but as of Beta 6 the JS styling kept clobbering CSS classes. Hence use this JS styling for now.
 */
export const SELECT_STYLE: StylesConfig = {
  control: (base, state: OptionProps) => ({
    ...base,
    cursor: 'pointer',
    backgroundColor: state.isDisabled ? '#e9ecef' : /* $gray-200 */ 'white',
    boxShadow: state.isFocused
      ? 'inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 3px rgba(35, 97, 142, 0.25);'
      : 'none',
    borderColor: state.isFocused ? '#5aa2d6' : base.borderColor,
    ':hover': {
      borderColor: state.isFocused ? '#5aa2d6' : base.borderColor,
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 10,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    alignItems: 'flex-end',
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#adb5bd',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#adb5bd',
  }),
  placeholder: (base) => ({
    ...base,
    fontStyle: 'italic',
    color: '#999',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#f2f9fc',
    borderRadius: '2px',
    border: '1px solid #c9e6f2',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#08c',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#08c',
    ':hover': {
      backgroundColor: '#004d6a',
    },
  }),
};

/**
 * A react-select style that makes the placeholder text look the same as a value
 */
export const DARK_PLACEHOLDER: StylesConfig = {
  placeholder: (base) => ({
    ...base,
    color: '#000',
  }),
};

export const DISABLED_PLACEHOLDER: StylesConfig = {
  placeholder: (base) => ({
    ...base,
    color: '#000',
  }),
  multiValueLabel: (base, state) => {
    return state.data.isDisabled
      ? {
          ...base,
          color: '#08c',
          opacity: 0.7,
          cursor: 'default',
          paddingRight: 6,
        }
      : { ...base, color: '#08c' };
  },
  // hide remove icon if option is marked not to be removed
  multiValueRemove: (base, state) => {
    return state.data.isDisabled
      ? {
          ...base,
          color: '#08c',
          ':hover': { backgroundColor: '#004d6a' },
          display: 'none',
        }
      : { ...base, color: '#08c', ':hover': { backgroundColor: '#004d6a' } };
  },
};

/**
 * A helper to clean up the return types of react-select's onChange handler
 * For multiple selects, return array or null; for single select, return option or null
 */
type GetNewValue = {
  <OT extends OptionTypeBase>(
    newValue: ValueType<OT>,
    isMulti: true,
  ): Array<OT> | null;
  <OT extends OptionTypeBase>(
    newValue: ValueType<OT>,
    isMulti: false,
  ): OT | null;
};

export const getNewValue: GetNewValue = <OT extends OptionType = OptionType>(
  newValue: ValueType<OT>,
  isMulti: boolean,
) => {
  if (!newValue) {
    return null;
  }
  return (
    (isMulti && Array.isArray(newValue) && newValue) ||
    (!isMulti && !Array.isArray(newValue) && newValue) ||
    null // it should never get here, this is mostly to cover all possible paths to please TS
  );
};

/**
 * Comparator factory for creating alphabetical comparators on object keys
 * @param {string} key - the object key to sort on
 * @return {function} - Array.sort compatible comparator function
 */
export const getAlphaComparator = <T extends { [key: string]: string }>(
  key: keyof T,
) => {
  return (a: T, b: T): number => {
    return (a[key] || '').localeCompare(b[key] || '');
  };
};

/**
 * Scales a number to the specified base and with the specified precision
 * @param {number|string} input - the number to scale
 * @param {number} base - the base to divide by
 * @param {number} maxDecimals - maximum digits after the decimal (precision)
 * @return {{num: number, suffix: string}} - a two-property object (Scaled) with the scaled number and unit suffix
 */
export const scaleValue = (
  input: number | string,
  base: number,
  maxDecimals: number,
): Scaled => {
  const value = typeof input !== 'number' ? parseFloat(input) : input;
  let index = 0,
    num: string;
  if (isNaN(value)) {
    num = MISSING_VALUE_PLACEHOLDER;
  } else {
    index = Math.floor(Math.log(value) / Math.log(base));
    index = Math.max(0, Math.min(index, SIZES.length - 1)); // lock index range between 0 and sizes.length-1
    num = (value / Math.pow(base, index))
      .toFixed(index > 0 ? maxDecimals : 0)
      .toString();
  }
  return { num, suffix: SIZES[index] };
};

/**
 * Retrieve value of a cookie, return null if not found
 * @param {string} name - name of the cookie
 * @return {string|null}
 */
export const getCookie = (name: string): string | null => {
  const cookie = document.cookie
    .split(/\s*;\s*/)
    .find((item) => item.trim().startsWith(name));
  if (!cookie) {
    return null;
  }
  return cookie.substring(name.length + 1);
};

/**
 * Get JWT from cookie (set by backend), and use it to update
 * the auth token in session storage used by future API requests.
 * Clears the cookie after use.
 */
export const setAuthTokenFromCookie = () => {
  const primaryCookieJWT = getCookie(PRIMARY_JWT_COOKIE_NAME);
  if (primaryCookieJWT) {
    window.sessionStorage.setItem(
      SessionStorageKey.PRIMARY_TOKEN,
      primaryCookieJWT,
    );
    document.cookie = `${PRIMARY_JWT_COOKIE_NAME}=;max-age=0;path=/;`;
  }
  const secondaryCookieJWT = getCookie(SECONDARY_JWT_COOKIE_NAME);
  if (secondaryCookieJWT) {
    window.sessionStorage.setItem(
      SessionStorageKey.SECONDARY_TOKEN,
      secondaryCookieJWT,
    );
    document.cookie = `${SECONDARY_JWT_COOKIE_NAME}=;max-age=0;path=/;`;
  }
  return {
    primaryCookieJWT,
    secondaryCookieJWT,
  };
};

/**
 * For Excel users
 * String containing commas need to be wrapped with double quotes, in which case
 *   the double quotes in the original string needs to be escaped as well.
 * e.g. `a "b", c` should become `"a ""b"", c"`
 */
export const escapeComma = (value: string) => {
  if (!value.includes(',')) {
    return value;
  }
  return `"${value.replace(/"/g, '""')}"`;
};

/**
 * Given a list of objects and the headers, create a csv and initiate download
 */
export const exportCSVFile = <T extends {}>(
  headers: Map<keyof T, string>,
  rowItems: T[],
  fileTitle = 'export.csv',
) => {
  const headerRow = Array.from(headers.values()).join(',');
  const orderedCols = Array.from(headers.keys());
  const contentRows = rowItems.map((rowItem) =>
    orderedCols.map((col) => escapeComma(`${rowItem[col]}`)).join(','),
  );
  const csv = [headerRow, ...contentRows].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  FileSaver.saveAs(blob, fileTitle);
};

/**
 * A workaround of TS false alarming setState with dynamic key name i.e. this.setState({[id]: value})
 * use this.setState(updateState<MyState>(id, value)) to only error when id is not a key of State
 * See https://github.com/DefinitelyTyped/DefinitelyTyped/issues/26635 for details
 */
export const updateState =
  <State, T extends string = string>(key: keyof State, value: T) =>
  (prevState: State): State => ({
    ...prevState,
    [key]: value,
  });

/**
 * A filter function to help TS infer the output to be defined & non-null
 * See https://stackoverflow.com/questions/43118692/typescript-filter-out-nulls-from-an-array
 */
function notEmpty<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

export const SUPPORTED_BROWSERS = {
  Chrome: 1,
  Firefox: 1,
  'Microsoft Edge': 1,
  Safari: 1,
  'Internet Explorer': 0, // 0 means it is no longer supported
  Opera: 1,
};

const userPermissions: {
  [T in PermissionEnum]: {
    services: Set<ServicesEnum>;
    roles: Set<UserRoleEnum>;
  };
} = {
  [PermissionEnum.VIEW_DASHBOARD]: {
    services: new Set([ServicesEnum.MDR, ServicesEnum.MR]),
    roles: new Set([UserRoleEnum.VIEW_DASHBOARD]),
  },
  [PermissionEnum.VIEW_DATA]: {
    services: new Set([ServicesEnum.MDR]),
    roles: new Set([UserRoleEnum.VIEW_DATA]),
  },
  [PermissionEnum.VIEW_LOG_SEARCH]: {
    services: new Set([ServicesEnum.LOGS]),
    roles: new Set([UserRoleEnum.VIEW_LOG_SEARCH]),
  },
  [PermissionEnum.VIEW_TOP_COUNTRIES]: {
    services: new Set([ServicesEnum.MDR]),
    roles: new Set([UserRoleEnum.VIEW_DASHBOARD]),
  },
  [PermissionEnum.VIEW_REPORTS]: {
    services: new Set([ServicesEnum.MDR]),
    roles: new Set([UserRoleEnum.VIEW_REPORTS]),
  },
};

/**
 * For the given roles, services and permission, determine if the user has the permissions
 * to see the view/component
 * @param activeRoles The currently active roles. e.g useSelector(selectUserRoles)
 * @param activeServices The currently active services. e.g useSelector(selectCustomerServices)
 * @param perm The permission to check against
 */
export const hasPermissions = (
  activeRoles: Set<UserRoleEnum>,
  activeServices: Set<ServicesEnum>,
  perm: PermissionEnum,
): boolean => {
  const { services, roles } = userPermissions[perm];

  let hasRolePermission = false;
  let hasServicePermission = false;

  for (const service of services) {
    if (activeServices.has(service)) {
      hasServicePermission = true;
      break;
    }
  }

  for (const role of roles) {
    if (activeRoles.has(role)) {
      hasRolePermission = true;
      break;
    }
  }

  return hasServicePermission && hasRolePermission;
};

export enum OnboardingSections {
  CHECKLIST = 'Checklist',
  CONTACTS = 'Contacts',
  SITE_CONFIG = 'Site Configuration',
  CLOUD_SENSOR_CONFIG = 'Cloud Configuration',
  INTERNET_EXPOSURE = 'Internet Exposure',
  ENDPOINT_CONFIG = 'Endpoint Configuration',
}

export enum ContactCategory {
  USER = 'user',
  DL = 'dl',
  SC = 'sc',
}

//Timezones changed to match the convention used in Unified Portal
export const TIMEZONES = [
  { label: 'Africa/Abidjan', value: 'Africa/Abidjan' },
  { label: 'Africa/Accra', value: 'Africa/Accra' },
  { label: 'Africa/Addis_Ababa', value: 'Africa/Addis_Ababa' },
  { label: 'Africa/Algiers', value: 'Africa/Algiers' },
  { label: 'Africa/Asmara', value: 'Africa/Asmara' },
  { label: 'Africa/Asmera', value: 'Africa/Asmera' },
  { label: 'Africa/Bamako', value: 'Africa/Bamako' },
  { label: 'Africa/Bangui', value: 'Africa/Bangui' },
  { label: 'Africa/Banjul', value: 'Africa/Banjul' },
  { label: 'Africa/Bissau', value: 'Africa/Bissau' },
  { label: 'Africa/Blantyre', value: 'Africa/Blantyre' },
  { label: 'Africa/Brazzaville', value: 'Africa/Brazzaville' },
  { label: 'Africa/Bujumbura', value: 'Africa/Bujumbura' },
  { label: 'Africa/Cairo', value: 'Africa/Cairo' },
  { label: 'Africa/Casablanca', value: 'Africa/Casablanca' },
  { label: 'Africa/Ceuta', value: 'Africa/Ceuta' },
  { label: 'Africa/Conakry', value: 'Africa/Conakry' },
  { label: 'Africa/Dakar', value: 'Africa/Dakar' },
  { label: 'Africa/Dar_es_Salaam', value: 'Africa/Dar_es_Salaam' },
  { label: 'Africa/Djibouti', value: 'Africa/Djibouti' },
  { label: 'Africa/Douala', value: 'Africa/Douala' },
  { label: 'Africa/El_Aaiun', value: 'Africa/El_Aaiun' },
  { label: 'Africa/Freetown', value: 'Africa/Freetown' },
  { label: 'Africa/Gaborone', value: 'Africa/Gaborone' },
  { label: 'Africa/Harare', value: 'Africa/Harare' },
  { label: 'Africa/Johannesburg', value: 'Africa/Johannesburg' },
  { label: 'Africa/Juba', value: 'Africa/Juba' },
  { label: 'Africa/Kampala', value: 'Africa/Kampala' },
  { label: 'Africa/Khartoum', value: 'Africa/Khartoum' },
  { label: 'Africa/Kigali', value: 'Africa/Kigali' },
  { label: 'Africa/Kinshasa', value: 'Africa/Kinshasa' },
  { label: 'Africa/Lagos', value: 'Africa/Lagos' },
  { label: 'Africa/Libreville', value: 'Africa/Libreville' },
  { label: 'Africa/Lome', value: 'Africa/Lome' },
  { label: 'Africa/Luanda', value: 'Africa/Luanda' },
  { label: 'Africa/Lubumbashi', value: 'Africa/Lubumbashi' },
  { label: 'Africa/Lusaka', value: 'Africa/Lusaka' },
  { label: 'Africa/Malabo', value: 'Africa/Malabo' },
  { label: 'Africa/Maputo', value: 'Africa/Maputo' },
  { label: 'Africa/Maseru', value: 'Africa/Maseru' },
  { label: 'Africa/Mbabane', value: 'Africa/Mbabane' },
  { label: 'Africa/Mogadishu', value: 'Africa/Mogadishu' },
  { label: 'Africa/Monrovia', value: 'Africa/Monrovia' },
  { label: 'Africa/Nairobi', value: 'Africa/Nairobi' },
  { label: 'Africa/Ndjamena', value: 'Africa/Ndjamena' },
  { label: 'Africa/Niamey', value: 'Africa/Niamey' },
  { label: 'Africa/Nouakchott', value: 'Africa/Nouakchott' },
  { label: 'Africa/Ouagadougou', value: 'Africa/Ouagadougou' },
  { label: 'Africa/Porto-Novo', value: 'Africa/Porto-Novo' },
  { label: 'Africa/Sao_Tome', value: 'Africa/Sao_Tome' },
  { label: 'Africa/Timbuktu', value: 'Africa/Timbuktu' },
  { label: 'Africa/Tripoli', value: 'Africa/Tripoli' },
  { label: 'Africa/Tunis', value: 'Africa/Tunis' },
  { label: 'Africa/Windhoek', value: 'Africa/Windhoek' },
  { label: 'America/Adak', value: 'America/Adak' },
  { label: 'America/Anchorage', value: 'America/Anchorage' },
  { label: 'America/Anguilla', value: 'America/Anguilla' },
  { label: 'America/Antigua', value: 'America/Antigua' },
  { label: 'America/Araguaina', value: 'America/Araguaina' },
  {
    label: 'America/Argentina/Buenos_Aires',
    value: 'America/Argentina/Buenos_Aires',
  },
  {
    label: 'America/Argentina/Catamarca',
    value: 'America/Argentina/Catamarca',
  },
  {
    label: 'America/Argentina/ComodRivadavia',
    value: 'America/Argentina/ComodRivadavia',
  },
  { label: 'America/Argentina/Cordoba', value: 'America/Argentina/Cordoba' },
  { label: 'America/Argentina/Jujuy', value: 'America/Argentina/Jujuy' },
  { label: 'America/Argentina/La_Rioja', value: 'America/Argentina/La_Rioja' },
  { label: 'America/Argentina/Mendoza', value: 'America/Argentina/Mendoza' },
  {
    label: 'America/Argentina/Rio_Gallegos',
    value: 'America/Argentina/Rio_Gallegos',
  },
  { label: 'America/Argentina/Salta', value: 'America/Argentina/Salta' },
  { label: 'America/Argentina/San_Juan', value: 'America/Argentina/San_Juan' },
  { label: 'America/Argentina/San_Luis', value: 'America/Argentina/San_Luis' },
  { label: 'America/Argentina/Tucuman', value: 'America/Argentina/Tucuman' },
  { label: 'America/Argentina/Ushuaia', value: 'America/Argentina/Ushuaia' },
  { label: 'America/Aruba', value: 'America/Aruba' },
  { label: 'America/Asuncion', value: 'America/Asuncion' },
  { label: 'America/Atikokan', value: 'America/Atikokan' },
  { label: 'America/Atka', value: 'America/Atka' },
  { label: 'America/Bahia', value: 'America/Bahia' },
  { label: 'America/Bahia_Banderas', value: 'America/Bahia_Banderas' },
  { label: 'America/Barbados', value: 'America/Barbados' },
  { label: 'America/Belem', value: 'America/Belem' },
  { label: 'America/Belize', value: 'America/Belize' },
  { label: 'America/Blanc-Sablon', value: 'America/Blanc-Sablon' },
  { label: 'America/Boa_Vista', value: 'America/Boa_Vista' },
  { label: 'America/Bogota', value: 'America/Bogota' },
  { label: 'America/Boise', value: 'America/Boise' },
  { label: 'America/Buenos_Aires', value: 'America/Buenos_Aires' },
  { label: 'America/Cambridge_Bay', value: 'America/Cambridge_Bay' },
  { label: 'America/Campo_Grande', value: 'America/Campo_Grande' },
  { label: 'America/Cancun', value: 'America/Cancun' },
  { label: 'America/Caracas', value: 'America/Caracas' },
  { label: 'America/Catamarca', value: 'America/Catamarca' },
  { label: 'America/Cayenne', value: 'America/Cayenne' },
  { label: 'America/Cayman', value: 'America/Cayman' },
  { label: 'America/Chicago', value: 'America/Chicago' },
  { label: 'America/Chihuahua', value: 'America/Chihuahua' },
  { label: 'America/Ciudad_Juarez', value: 'America/Ciudad_Juarez' },
  { label: 'America/Coral_Harbour', value: 'America/Coral_Harbour' },
  { label: 'America/Cordoba', value: 'America/Cordoba' },
  { label: 'America/Costa_Rica', value: 'America/Costa_Rica' },
  { label: 'America/Creston', value: 'America/Creston' },
  { label: 'America/Cuiaba', value: 'America/Cuiaba' },
  { label: 'America/Curacao', value: 'America/Curacao' },
  { label: 'America/Danmarkshavn', value: 'America/Danmarkshavn' },
  { label: 'America/Dawson', value: 'America/Dawson' },
  { label: 'America/Dawson_Creek', value: 'America/Dawson_Creek' },
  { label: 'America/Denver', value: 'America/Denver' },
  { label: 'America/Detroit', value: 'America/Detroit' },
  { label: 'America/Dominica', value: 'America/Dominica' },
  { label: 'America/Edmonton', value: 'America/Edmonton' },
  { label: 'America/Eirunepe', value: 'America/Eirunepe' },
  { label: 'America/El_Salvador', value: 'America/El_Salvador' },
  { label: 'America/Ensenada', value: 'America/Ensenada' },
  { label: 'America/Fort_Nelson', value: 'America/Fort_Nelson' },
  { label: 'America/Fort_Wayne', value: 'America/Fort_Wayne' },
  { label: 'America/Fortaleza', value: 'America/Fortaleza' },
  { label: 'America/Glace_Bay', value: 'America/Glace_Bay' },
  { label: 'America/Godthab', value: 'America/Godthab' },
  { label: 'America/Goose_Bay', value: 'America/Goose_Bay' },
  { label: 'America/Grand_Turk', value: 'America/Grand_Turk' },
  { label: 'America/Grenada', value: 'America/Grenada' },
  { label: 'America/Guadeloupe', value: 'America/Guadeloupe' },
  { label: 'America/Guatemala', value: 'America/Guatemala' },
  { label: 'America/Guayaquil', value: 'America/Guayaquil' },
  { label: 'America/Guyana', value: 'America/Guyana' },
  { label: 'America/Halifax', value: 'America/Halifax' },
  { label: 'America/Havana', value: 'America/Havana' },
  { label: 'America/Hermosillo', value: 'America/Hermosillo' },
  {
    label: 'America/Indiana/Indianapolis',
    value: 'America/Indiana/Indianapolis',
  },
  { label: 'America/Indiana/Knox', value: 'America/Indiana/Knox' },
  { label: 'America/Indiana/Marengo', value: 'America/Indiana/Marengo' },
  { label: 'America/Indiana/Petersburg', value: 'America/Indiana/Petersburg' },
  { label: 'America/Indiana/Tell_City', value: 'America/Indiana/Tell_City' },
  { label: 'America/Indiana/Vevay', value: 'America/Indiana/Vevay' },
  { label: 'America/Indiana/Vincennes', value: 'America/Indiana/Vincennes' },
  { label: 'America/Indiana/Winamac', value: 'America/Indiana/Winamac' },
  { label: 'America/Indianapolis', value: 'America/Indianapolis' },
  { label: 'America/Inuvik', value: 'America/Inuvik' },
  { label: 'America/Iqaluit', value: 'America/Iqaluit' },
  { label: 'America/Jamaica', value: 'America/Jamaica' },
  { label: 'America/Jujuy', value: 'America/Jujuy' },
  { label: 'America/Juneau', value: 'America/Juneau' },
  {
    label: 'America/Kentucky/Louisville',
    value: 'America/Kentucky/Louisville',
  },
  {
    label: 'America/Kentucky/Monticello',
    value: 'America/Kentucky/Monticello',
  },
  { label: 'America/Knox_IN', value: 'America/Knox_IN' },
  { label: 'America/Kralendijk', value: 'America/Kralendijk' },
  { label: 'America/La_Paz', value: 'America/La_Paz' },
  { label: 'America/Lima', value: 'America/Lima' },
  { label: 'America/Los_Angeles', value: 'America/Los_Angeles' },
  { label: 'America/Louisville', value: 'America/Louisville' },
  { label: 'America/Lower_Princes', value: 'America/Lower_Princes' },
  { label: 'America/Maceio', value: 'America/Maceio' },
  { label: 'America/Managua', value: 'America/Managua' },
  { label: 'America/Manaus', value: 'America/Manaus' },
  { label: 'America/Marigot', value: 'America/Marigot' },
  { label: 'America/Martinique', value: 'America/Martinique' },
  { label: 'America/Matamoros', value: 'America/Matamoros' },
  { label: 'America/Mazatlan', value: 'America/Mazatlan' },
  { label: 'America/Mendoza', value: 'America/Mendoza' },
  { label: 'America/Menominee', value: 'America/Menominee' },
  { label: 'America/Merida', value: 'America/Merida' },
  { label: 'America/Metlakatla', value: 'America/Metlakatla' },
  { label: 'America/Mexico_City', value: 'America/Mexico_City' },
  { label: 'America/Miquelon', value: 'America/Miquelon' },
  { label: 'America/Moncton', value: 'America/Moncton' },
  { label: 'America/Monterrey', value: 'America/Monterrey' },
  { label: 'America/Montevideo', value: 'America/Montevideo' },
  { label: 'America/Montreal', value: 'America/Montreal' },
  { label: 'America/Montserrat', value: 'America/Montserrat' },
  { label: 'America/Nassau', value: 'America/Nassau' },
  { label: 'America/New_York', value: 'America/New_York' },
  { label: 'America/Nipigon', value: 'America/Nipigon' },
  { label: 'America/Nome', value: 'America/Nome' },
  { label: 'America/Noronha', value: 'America/Noronha' },
  {
    label: 'America/North_Dakota/Beulah',
    value: 'America/North_Dakota/Beulah',
  },
  {
    label: 'America/North_Dakota/Center',
    value: 'America/North_Dakota/Center',
  },
  {
    label: 'America/North_Dakota/New_Salem',
    value: 'America/North_Dakota/New_Salem',
  },
  { label: 'America/Nuuk', value: 'America/Nuuk' },
  { label: 'America/Ojinaga', value: 'America/Ojinaga' },
  { label: 'America/Panama', value: 'America/Panama' },
  { label: 'America/Pangnirtung', value: 'America/Pangnirtung' },
  { label: 'America/Paramaribo', value: 'America/Paramaribo' },
  { label: 'America/Phoenix', value: 'America/Phoenix' },
  { label: 'America/Port-au-Prince', value: 'America/Port-au-Prince' },
  { label: 'America/Port_of_Spain', value: 'America/Port_of_Spain' },
  { label: 'America/Porto_Acre', value: 'America/Porto_Acre' },
  { label: 'America/Porto_Velho', value: 'America/Porto_Velho' },
  { label: 'America/Puerto_Rico', value: 'America/Puerto_Rico' },
  { label: 'America/Punta_Arenas', value: 'America/Punta_Arenas' },
  { label: 'America/Rainy_River', value: 'America/Rainy_River' },
  { label: 'America/Rankin_Inlet', value: 'America/Rankin_Inlet' },
  { label: 'America/Recife', value: 'America/Recife' },
  { label: 'America/Regina', value: 'America/Regina' },
  { label: 'America/Resolute', value: 'America/Resolute' },
  { label: 'America/Rio_Branco', value: 'America/Rio_Branco' },
  { label: 'America/Rosario', value: 'America/Rosario' },
  { label: 'America/Santa_Isabel', value: 'America/Santa_Isabel' },
  { label: 'America/Santarem', value: 'America/Santarem' },
  { label: 'America/Santiago', value: 'America/Santiago' },
  { label: 'America/Santo_Domingo', value: 'America/Santo_Domingo' },
  { label: 'America/Sao_Paulo', value: 'America/Sao_Paulo' },
  { label: 'America/Scoresbysund', value: 'America/Scoresbysund' },
  { label: 'America/Shiprock', value: 'America/Shiprock' },
  { label: 'America/Sitka', value: 'America/Sitka' },
  { label: 'America/St_Barthelemy', value: 'America/St_Barthelemy' },
  { label: 'America/St_Johns', value: 'America/St_Johns' },
  { label: 'America/St_Kitts', value: 'America/St_Kitts' },
  { label: 'America/St_Lucia', value: 'America/St_Lucia' },
  { label: 'America/St_Thomas', value: 'America/St_Thomas' },
  { label: 'America/St_Vincent', value: 'America/St_Vincent' },
  { label: 'America/Swift_Current', value: 'America/Swift_Current' },
  { label: 'America/Tegucigalpa', value: 'America/Tegucigalpa' },
  { label: 'America/Thule', value: 'America/Thule' },
  { label: 'America/Thunder_Bay', value: 'America/Thunder_Bay' },
  { label: 'America/Tijuana', value: 'America/Tijuana' },
  { label: 'America/Toronto', value: 'America/Toronto' },
  { label: 'America/Tortola', value: 'America/Tortola' },
  { label: 'America/Vancouver', value: 'America/Vancouver' },
  { label: 'America/Virgin', value: 'America/Virgin' },
  { label: 'America/Whitehorse', value: 'America/Whitehorse' },
  { label: 'America/Winnipeg', value: 'America/Winnipeg' },
  { label: 'America/Yakutat', value: 'America/Yakutat' },
  { label: 'America/Yellowknife', value: 'America/Yellowknife' },
  { label: 'Antarctica/Casey', value: 'Antarctica/Casey' },
  { label: 'Antarctica/Davis', value: 'Antarctica/Davis' },
  { label: 'Antarctica/DumontDUrville', value: 'Antarctica/DumontDUrville' },
  { label: 'Antarctica/Macquarie', value: 'Antarctica/Macquarie' },
  { label: 'Antarctica/Mawson', value: 'Antarctica/Mawson' },
  { label: 'Antarctica/McMurdo', value: 'Antarctica/McMurdo' },
  { label: 'Antarctica/Palmer', value: 'Antarctica/Palmer' },
  { label: 'Antarctica/Rothera', value: 'Antarctica/Rothera' },
  { label: 'Antarctica/South_Pole', value: 'Antarctica/South_Pole' },
  { label: 'Antarctica/Syowa', value: 'Antarctica/Syowa' },
  { label: 'Antarctica/Troll', value: 'Antarctica/Troll' },
  { label: 'Antarctica/Vostok', value: 'Antarctica/Vostok' },
  { label: 'Arctic/Longyearbyen', value: 'Arctic/Longyearbyen' },
  { label: 'Asia/Aden', value: 'Asia/Aden' },
  { label: 'Asia/Almaty', value: 'Asia/Almaty' },
  { label: 'Asia/Amman', value: 'Asia/Amman' },
  { label: 'Asia/Anadyr', value: 'Asia/Anadyr' },
  { label: 'Asia/Aqtau', value: 'Asia/Aqtau' },
  { label: 'Asia/Aqtobe', value: 'Asia/Aqtobe' },
  { label: 'Asia/Ashgabat', value: 'Asia/Ashgabat' },
  { label: 'Asia/Ashkhabad', value: 'Asia/Ashkhabad' },
  { label: 'Asia/Atyrau', value: 'Asia/Atyrau' },
  { label: 'Asia/Baghdad', value: 'Asia/Baghdad' },
  { label: 'Asia/Bahrain', value: 'Asia/Bahrain' },
  { label: 'Asia/Baku', value: 'Asia/Baku' },
  { label: 'Asia/Bangkok', value: 'Asia/Bangkok' },
  { label: 'Asia/Barnaul', value: 'Asia/Barnaul' },
  { label: 'Asia/Beirut', value: 'Asia/Beirut' },
  { label: 'Asia/Bishkek', value: 'Asia/Bishkek' },
  { label: 'Asia/Brunei', value: 'Asia/Brunei' },
  { label: 'Asia/Calcutta', value: 'Asia/Calcutta' },
  { label: 'Asia/Chita', value: 'Asia/Chita' },
  { label: 'Asia/Choibalsan', value: 'Asia/Choibalsan' },
  { label: 'Asia/Chongqing', value: 'Asia/Chongqing' },
  { label: 'Asia/Chungking', value: 'Asia/Chungking' },
  { label: 'Asia/Colombo', value: 'Asia/Colombo' },
  { label: 'Asia/Dacca', value: 'Asia/Dacca' },
  { label: 'Asia/Damascus', value: 'Asia/Damascus' },
  { label: 'Asia/Dhaka', value: 'Asia/Dhaka' },
  { label: 'Asia/Dili', value: 'Asia/Dili' },
  { label: 'Asia/Dubai', value: 'Asia/Dubai' },
  { label: 'Asia/Dushanbe', value: 'Asia/Dushanbe' },
  { label: 'Asia/Famagusta', value: 'Asia/Famagusta' },
  { label: 'Asia/Gaza', value: 'Asia/Gaza' },
  { label: 'Asia/Harbin', value: 'Asia/Harbin' },
  { label: 'Asia/Hebron', value: 'Asia/Hebron' },
  { label: 'Asia/Ho_Chi_Minh', value: 'Asia/Ho_Chi_Minh' },
  { label: 'Asia/Hong_Kong', value: 'Asia/Hong_Kong' },
  { label: 'Asia/Hovd', value: 'Asia/Hovd' },
  { label: 'Asia/Irkutsk', value: 'Asia/Irkutsk' },
  { label: 'Asia/Istanbul', value: 'Asia/Istanbul' },
  { label: 'Asia/Jakarta', value: 'Asia/Jakarta' },
  { label: 'Asia/Jayapura', value: 'Asia/Jayapura' },
  { label: 'Asia/Jerusalem', value: 'Asia/Jerusalem' },
  { label: 'Asia/Kabul', value: 'Asia/Kabul' },
  { label: 'Asia/Kamchatka', value: 'Asia/Kamchatka' },
  { label: 'Asia/Karachi', value: 'Asia/Karachi' },
  { label: 'Asia/Kashgar', value: 'Asia/Kashgar' },
  { label: 'Asia/Kathmandu', value: 'Asia/Kathmandu' },
  { label: 'Asia/Katmandu', value: 'Asia/Katmandu' },
  { label: 'Asia/Khandyga', value: 'Asia/Khandyga' },
  { label: 'Asia/Kolkata', value: 'Asia/Kolkata' },
  { label: 'Asia/Krasnoyarsk', value: 'Asia/Krasnoyarsk' },
  { label: 'Asia/Kuala_Lumpur', value: 'Asia/Kuala_Lumpur' },
  { label: 'Asia/Kuching', value: 'Asia/Kuching' },
  { label: 'Asia/Kuwait', value: 'Asia/Kuwait' },
  { label: 'Asia/Macao', value: 'Asia/Macao' },
  { label: 'Asia/Macau', value: 'Asia/Macau' },
  { label: 'Asia/Magadan', value: 'Asia/Magadan' },
  { label: 'Asia/Makassar', value: 'Asia/Makassar' },
  { label: 'Asia/Manila', value: 'Asia/Manila' },
  { label: 'Asia/Muscat', value: 'Asia/Muscat' },
  { label: 'Asia/Nicosia', value: 'Asia/Nicosia' },
  { label: 'Asia/Novokuznetsk', value: 'Asia/Novokuznetsk' },
  { label: 'Asia/Novosibirsk', value: 'Asia/Novosibirsk' },
  { label: 'Asia/Omsk', value: 'Asia/Omsk' },
  { label: 'Asia/Oral', value: 'Asia/Oral' },
  { label: 'Asia/Phnom_Penh', value: 'Asia/Phnom_Penh' },
  { label: 'Asia/Pontianak', value: 'Asia/Pontianak' },
  { label: 'Asia/Pyongyang', value: 'Asia/Pyongyang' },
  { label: 'Asia/Qatar', value: 'Asia/Qatar' },
  { label: 'Asia/Qostanay', value: 'Asia/Qostanay' },
  { label: 'Asia/Qyzylorda', value: 'Asia/Qyzylorda' },
  { label: 'Asia/Rangoon', value: 'Asia/Rangoon' },
  { label: 'Asia/Riyadh', value: 'Asia/Riyadh' },
  { label: 'Asia/Saigon', value: 'Asia/Saigon' },
  { label: 'Asia/Sakhalin', value: 'Asia/Sakhalin' },
  { label: 'Asia/Samarkand', value: 'Asia/Samarkand' },
  { label: 'Asia/Seoul', value: 'Asia/Seoul' },
  { label: 'Asia/Shanghai', value: 'Asia/Shanghai' },
  { label: 'Asia/Singapore', value: 'Asia/Singapore' },
  { label: 'Asia/Srednekolymsk', value: 'Asia/Srednekolymsk' },
  { label: 'Asia/Taipei', value: 'Asia/Taipei' },
  { label: 'Asia/Tashkent', value: 'Asia/Tashkent' },
  { label: 'Asia/Tbilisi', value: 'Asia/Tbilisi' },
  { label: 'Asia/Tehran', value: 'Asia/Tehran' },
  { label: 'Asia/Tel_Aviv', value: 'Asia/Tel_Aviv' },
  { label: 'Asia/Thimbu', value: 'Asia/Thimbu' },
  { label: 'Asia/Thimphu', value: 'Asia/Thimphu' },
  { label: 'Asia/Tokyo', value: 'Asia/Tokyo' },
  { label: 'Asia/Tomsk', value: 'Asia/Tomsk' },
  { label: 'Asia/Ujung_Pandang', value: 'Asia/Ujung_Pandang' },
  { label: 'Asia/Ulaanbaatar', value: 'Asia/Ulaanbaatar' },
  { label: 'Asia/Ulan_Bator', value: 'Asia/Ulan_Bator' },
  { label: 'Asia/Urumqi', value: 'Asia/Urumqi' },
  { label: 'Asia/Ust-Nera', value: 'Asia/Ust-Nera' },
  { label: 'Asia/Vientiane', value: 'Asia/Vientiane' },
  { label: 'Asia/Vladivostok', value: 'Asia/Vladivostok' },
  { label: 'Asia/Yakutsk', value: 'Asia/Yakutsk' },
  { label: 'Asia/Yangon', value: 'Asia/Yangon' },
  { label: 'Asia/Yekaterinburg', value: 'Asia/Yekaterinburg' },
  { label: 'Asia/Yerevan', value: 'Asia/Yerevan' },
  { label: 'Atlantic/Azores', value: 'Atlantic/Azores' },
  { label: 'Atlantic/Bermuda', value: 'Atlantic/Bermuda' },
  { label: 'Atlantic/Canary', value: 'Atlantic/Canary' },
  { label: 'Atlantic/Cape_Verde', value: 'Atlantic/Cape_Verde' },
  { label: 'Atlantic/Faeroe', value: 'Atlantic/Faeroe' },
  { label: 'Atlantic/Faroe', value: 'Atlantic/Faroe' },
  { label: 'Atlantic/Jan_Mayen', value: 'Atlantic/Jan_Mayen' },
  { label: 'Atlantic/Madeira', value: 'Atlantic/Madeira' },
  { label: 'Atlantic/Reykjavik', value: 'Atlantic/Reykjavik' },
  { label: 'Atlantic/South_Georgia', value: 'Atlantic/South_Georgia' },
  { label: 'Atlantic/St_Helena', value: 'Atlantic/St_Helena' },
  { label: 'Atlantic/Stanley', value: 'Atlantic/Stanley' },
  { label: 'Australia/ACT', value: 'Australia/ACT' },
  { label: 'Australia/Adelaide', value: 'Australia/Adelaide' },
  { label: 'Australia/Brisbane', value: 'Australia/Brisbane' },
  { label: 'Australia/Broken_Hill', value: 'Australia/Broken_Hill' },
  { label: 'Australia/Canberra', value: 'Australia/Canberra' },
  { label: 'Australia/Currie', value: 'Australia/Currie' },
  { label: 'Australia/Darwin', value: 'Australia/Darwin' },
  { label: 'Australia/Eucla', value: 'Australia/Eucla' },
  { label: 'Australia/Hobart', value: 'Australia/Hobart' },
  { label: 'Australia/LHI', value: 'Australia/LHI' },
  { label: 'Australia/Lindeman', value: 'Australia/Lindeman' },
  { label: 'Australia/Lord_Howe', value: 'Australia/Lord_Howe' },
  { label: 'Australia/Melbourne', value: 'Australia/Melbourne' },
  { label: 'Australia/NSW', value: 'Australia/NSW' },
  { label: 'Australia/North', value: 'Australia/North' },
  { label: 'Australia/Perth', value: 'Australia/Perth' },
  { label: 'Australia/Queensland', value: 'Australia/Queensland' },
  { label: 'Australia/South', value: 'Australia/South' },
  { label: 'Australia/Sydney', value: 'Australia/Sydney' },
  { label: 'Australia/Tasmania', value: 'Australia/Tasmania' },
  { label: 'Australia/Victoria', value: 'Australia/Victoria' },
  { label: 'Australia/West', value: 'Australia/West' },
  { label: 'Australia/Yancowinna', value: 'Australia/Yancowinna' },
  { label: 'Brazil/Acre', value: 'Brazil/Acre' },
  { label: 'Brazil/DeNoronha', value: 'Brazil/DeNoronha' },
  { label: 'Brazil/East', value: 'Brazil/East' },
  { label: 'Brazil/West', value: 'Brazil/West' },
  // 'CET',
  // 'CST6CDT',
  { label: 'Canada/Atlantic', value: 'Canada/Atlantic' },
  { label: 'Canada/Central', value: 'Canada/Central' },
  { label: 'Canada/Eastern', value: 'Canada/Eastern' },
  { label: 'Canada/Mountain', value: 'Canada/Mountain' },
  { label: 'Canada/Newfoundland', value: 'Canada/Newfoundland' },
  { label: 'Canada/Pacific', value: 'Canada/Pacific' },
  { label: 'Canada/Saskatchewan', value: 'Canada/Saskatchewan' },
  { label: 'Canada/Yukon', value: 'Canada/Yukon' },
  { label: 'Chile/Continental', value: 'Chile/Continental' },
  { label: 'Chile/EasterIsland', value: 'Chile/EasterIsland' },
  { label: 'Cuba', value: 'Cuba' },
  // 'EET',
  // 'EST',
  // 'EST5EDT',
  { label: 'Egypt', value: 'Egypt' },
  { label: 'Eire', value: 'Eire' },
  { label: 'Etc/GMT', value: 'Etc/GMT' },
  { label: 'Etc/GMT+0', value: 'Etc/GMT+0' },
  { label: 'Etc/GMT+1', value: 'Etc/GMT+1' },
  { label: 'Etc/GMT+10', value: 'Etc/GMT+10' },
  { label: 'Etc/GMT+11', value: 'Etc/GMT+11' },
  { label: 'Etc/GMT+12', value: 'Etc/GMT+12' },
  { label: 'Etc/GMT+2', value: 'Etc/GMT+2' },
  { label: 'Etc/GMT+3', value: 'Etc/GMT+3' },
  { label: 'Etc/GMT+4', value: 'Etc/GMT+4' },
  { label: 'Etc/GMT+5', value: 'Etc/GMT+5' },
  { label: 'Etc/GMT+6', value: 'Etc/GMT+6' },
  { label: 'Etc/GMT+7', value: 'Etc/GMT+7' },
  { label: 'Etc/GMT+8', value: 'Etc/GMT+8' },
  { label: 'Etc/GMT+9', value: 'Etc/GMT+9' },
  { label: 'Etc/GMT-0', value: 'Etc/GMT-0' },
  { label: 'Etc/GMT-1', value: 'Etc/GMT-1' },
  { label: 'Etc/GMT-10', value: 'Etc/GMT-10' },
  { label: 'Etc/GMT-11', value: 'Etc/GMT-11' },
  { label: 'Etc/GMT-12', value: 'Etc/GMT-12' },
  { label: 'Etc/GMT-13', value: 'Etc/GMT-13' },
  { label: 'Etc/GMT-14', value: 'Etc/GMT-14' },
  { label: 'Etc/GMT-2', value: 'Etc/GMT-2' },
  { label: 'Etc/GMT-3', value: 'Etc/GMT-3' },
  { label: 'Etc/GMT-4', value: 'Etc/GMT-4' },
  { label: 'Etc/GMT-5', value: 'Etc/GMT-5' },
  { label: 'Etc/GMT-6', value: 'Etc/GMT-6' },
  { label: 'Etc/GMT-7', value: 'Etc/GMT-7' },
  { label: 'Etc/GMT-8', value: 'Etc/GMT-8' },
  { label: 'Etc/GMT-9', value: 'Etc/GMT-9' },
  { label: 'Etc/GMT0', value: 'Etc/GMT0' },
  { label: 'Etc/Greenwich', value: 'Etc/Greenwich' },
  { label: 'Etc/UCT', value: 'Etc/UCT' },
  { label: 'Etc/UTC', value: 'Etc/UTC' },
  { label: 'Etc/Universal', value: 'Etc/Universal' },
  { label: 'Etc/Zulu', value: 'Etc/Zulu' },
  { label: 'Europe/Amsterdam', value: 'Europe/Amsterdam' },
  { label: 'Europe/Andorra', value: 'Europe/Andorra' },
  { label: 'Europe/Astrakhan', value: 'Europe/Astrakhan' },
  { label: 'Europe/Athens', value: 'Europe/Athens' },
  { label: 'Europe/Belfast', value: 'Europe/Belfast' },
  { label: 'Europe/Belgrade', value: 'Europe/Belgrade' },
  { label: 'Europe/Berlin', value: 'Europe/Berlin' },
  { label: 'Europe/Bratislava', value: 'Europe/Bratislava' },
  { label: 'Europe/Brussels', value: 'Europe/Brussels' },
  { label: 'Europe/Bucharest', value: 'Europe/Bucharest' },
  { label: 'Europe/Budapest', value: 'Europe/Budapest' },
  { label: 'Europe/Busingen', value: 'Europe/Busingen' },
  { label: 'Europe/Chisinau', value: 'Europe/Chisinau' },
  { label: 'Europe/Copenhagen', value: 'Europe/Copenhagen' },
  { label: 'Europe/Dublin', value: 'Europe/Dublin' },
  { label: 'Europe/Gibraltar', value: 'Europe/Gibraltar' },
  { label: 'Europe/Guernsey', value: 'Europe/Guernsey' },
  { label: 'Europe/Helsinki', value: 'Europe/Helsinki' },
  { label: 'Europe/Isle_of_Man', value: 'Europe/Isle_of_Man' },
  { label: 'Europe/Istanbul', value: 'Europe/Istanbul' },
  { label: 'Europe/Jersey', value: 'Europe/Jersey' },
  { label: 'Europe/Kaliningrad', value: 'Europe/Kaliningrad' },
  { label: 'Europe/Kiev', value: 'Europe/Kiev' },
  { label: 'Europe/Kirov', value: 'Europe/Kirov' },
  { label: 'Europe/Kyiv', value: 'Europe/Kyiv' },
  { label: 'Europe/Lisbon', value: 'Europe/Lisbon' },
  { label: 'Europe/Ljubljana', value: 'Europe/Ljubljana' },
  { label: 'Europe/London', value: 'Europe/London' },
  { label: 'Europe/Luxembourg', value: 'Europe/Luxembourg' },
  { label: 'Europe/Madrid', value: 'Europe/Madrid' },
  { label: 'Europe/Malta', value: 'Europe/Malta' },
  { label: 'Europe/Mariehamn', value: 'Europe/Mariehamn' },
  { label: 'Europe/Minsk', value: 'Europe/Minsk' },
  { label: 'Europe/Monaco', value: 'Europe/Monaco' },
  { label: 'Europe/Moscow', value: 'Europe/Moscow' },
  { label: 'Europe/Nicosia', value: 'Europe/Nicosia' },
  { label: 'Europe/Oslo', value: 'Europe/Oslo' },
  { label: 'Europe/Paris', value: 'Europe/Paris' },
  { label: 'Europe/Podgorica', value: 'Europe/Podgorica' },
  { label: 'Europe/Prague', value: 'Europe/Prague' },
  { label: 'Europe/Riga', value: 'Europe/Riga' },
  { label: 'Europe/Rome', value: 'Europe/Rome' },
  { label: 'Europe/Samara', value: 'Europe/Samara' },
  { label: 'Europe/San_Marino', value: 'Europe/San_Marino' },
  { label: 'Europe/Sarajevo', value: 'Europe/Sarajevo' },
  { label: 'Europe/Saratov', value: 'Europe/Saratov' },
  { label: 'Europe/Simferopol', value: 'Europe/Simferopol' },
  { label: 'Europe/Skopje', value: 'Europe/Skopje' },
  { label: 'Europe/Sofia', value: 'Europe/Sofia' },
  { label: 'Europe/Stockholm', value: 'Europe/Stockholm' },
  { label: 'Europe/Tallinn', value: 'Europe/Tallinn' },
  { label: 'Europe/Tirane', value: 'Europe/Tirane' },
  { label: 'Europe/Tiraspol', value: 'Europe/Tiraspol' },
  { label: 'Europe/Ulyanovsk', value: 'Europe/Ulyanovsk' },
  { label: 'Europe/Uzhgorod', value: 'Europe/Uzhgorod' },
  { label: 'Europe/Vaduz', value: 'Europe/Vaduz' },
  { label: 'Europe/Vatican', value: 'Europe/Vatican' },
  { label: 'Europe/Vienna', value: 'Europe/Vienna' },
  { label: 'Europe/Vilnius', value: 'Europe/Vilnius' },
  { label: 'Europe/Volgograd', value: 'Europe/Volgograd' },
  { label: 'Europe/Warsaw', value: 'Europe/Warsaw' },
  { label: 'Europe/Zagreb', value: 'Europe/Zagreb' },
  { label: 'Europe/Zaporozhye', value: 'Europe/Zaporozhye' },
  { label: 'Europe/Zurich', value: 'Europe/Zurich' },
  { label: 'GB', value: 'GB' },
  { label: 'GB-Eire', value: 'GB-Eire' },
  // 'GMT'
  // 'GMT+0'
  // 'GMT-0'
  // 'GMT0'
  { label: 'Greenwich', value: 'Greenwich' },
  // 'HST'
  { label: 'Hongkong', value: 'Hongkong' },
  { label: 'Iceland', value: 'Iceland' },
  { label: 'Indian/Antananarivo', value: 'Indian/Antananarivo' },
  { label: 'Indian/Chagos', value: 'Indian/Chagos' },
  { label: 'Indian/Christmas', value: 'Indian/Christmas' },
  { label: 'Indian/Cocos', value: 'Indian/Cocos' },
  { label: 'Indian/Comoro', value: 'Indian/Comoro' },
  { label: 'Indian/Kerguelen', value: 'Indian/Kerguelen' },
  { label: 'Indian/Mahe', value: 'Indian/Mahe' },
  { label: 'Indian/Maldives', value: 'Indian/Maldives' },
  { label: 'Indian/Mauritius', value: 'Indian/Mauritius' },
  { label: 'Indian/Mayotte', value: 'Indian/Mayotte' },
  { label: 'Indian/Reunion', value: 'Indian/Reunion' },
  { label: 'Iran', value: 'Iran' },
  { label: 'Israel', value: 'Israel' },
  { label: 'Jamaica', value: 'Jamaica' },
  { label: 'Japan', value: 'Japan' },
  { label: 'Kwajalein', value: 'Kwajalein' },
  { label: 'Libya', value: 'Libya' },
  // 'MET'
  // 'MST'
  // 'MST7MDT'
  { label: 'Mexico/BajaNorte', value: 'Mexico/BajaNorte' },
  { label: 'Mexico/BajaSur', value: 'Mexico/BajaSur' },
  { label: 'Mexico/General', value: 'Mexico/General' },
  { label: 'NZ', value: 'NZ' },
  { label: 'NZ-CHAT', value: 'NZ-CHAT' },
  { label: 'Navajo', value: 'Navajo' },
  // 'PRC'
  // 'PST8PDT'
  { label: 'Pacific/Apia', value: 'Pacific/Apia' },
  { label: 'Pacific/Auckland', value: 'Pacific/Auckland' },
  { label: 'Pacific/Bougainville', value: 'Pacific/Bougainville' },
  { label: 'Pacific/Chatham', value: 'Pacific/Chatham' },
  { label: 'Pacific/Chuuk', value: 'Pacific/Chuuk' },
  { label: 'Pacific/Easter', value: 'Pacific/Easter' },
  { label: 'Pacific/Efate', value: 'Pacific/Efate' },
  { label: 'Pacific/Enderbury', value: 'Pacific/Enderbury' },
  { label: 'Pacific/Fakaofo', value: 'Pacific/Fakaofo' },
  { label: 'Pacific/Fiji', value: 'Pacific/Fiji' },
  { label: 'Pacific/Funafuti', value: 'Pacific/Funafuti' },
  { label: 'Pacific/Galapagos', value: 'Pacific/Galapagos' },
  { label: 'Pacific/Gambier', value: 'Pacific/Gambier' },
  { label: 'Pacific/Guadalcanal', value: 'Pacific/Guadalcanal' },
  { label: 'Pacific/Guam', value: 'Pacific/Guam' },
  { label: 'Pacific/Honolulu', value: 'Pacific/Honolulu' },
  { label: 'Pacific/Johnston', value: 'Pacific/Johnston' },
  { label: 'Pacific/Kanton', value: 'Pacific/Kanton' },
  { label: 'Pacific/Kiritimati', value: 'Pacific/Kiritimati' },
  { label: 'Pacific/Kosrae', value: 'Pacific/Kosrae' },
  { label: 'Pacific/Kwajalein', value: 'Pacific/Kwajalein' },
  { label: 'Pacific/Majuro', value: 'Pacific/Majuro' },
  { label: 'Pacific/Marquesas', value: 'Pacific/Marquesas' },
  { label: 'Pacific/Midway', value: 'Pacific/Midway' },
  { label: 'Pacific/Nauru', value: 'Pacific/Nauru' },
  { label: 'Pacific/Niue', value: 'Pacific/Niue' },
  { label: 'Pacific/Norfolk', value: 'Pacific/Norfolk' },
  { label: 'Pacific/Noumea', value: 'Pacific/Noumea' },
  { label: 'Pacific/Pago_Pago', value: 'Pacific/Pago_Pago' },
  { label: 'Pacific/Palau', value: 'Pacific/Palau' },
  { label: 'Pacific/Pitcairn', value: 'Pacific/Pitcairn' },
  { label: 'Pacific/Pohnpei', value: 'Pacific/Pohnpei' },
  { label: 'Pacific/Ponape', value: 'Pacific/Ponape' },
  { label: 'Pacific/Port_Moresby', value: 'Pacific/Port_Moresby' },
  { label: 'Pacific/Rarotonga', value: 'Pacific/Rarotonga' },
  { label: 'Pacific/Saipan', value: 'Pacific/Saipan' },
  { label: 'Pacific/Samoa', value: 'Pacific/Samoa' },
  { label: 'Pacific/Tahiti', value: 'Pacific/Tahiti' },
  { label: 'Pacific/Tarawa', value: 'Pacific/Tarawa' },
  { label: 'Pacific/Tongatapu', value: 'Pacific/Tongatapu' },
  { label: 'Pacific/Truk', value: 'Pacific/Truk' },
  { label: 'Pacific/Wake', value: 'Pacific/Wake' },
  { label: 'Pacific/Wallis', value: 'Pacific/Wallis' },
  { label: 'Pacific/Yap', value: 'Pacific/Yap' },
  { label: 'Poland', value: 'Poland' },
  { label: 'Portugal', value: 'Portugal' },
  { label: 'ROC', value: 'ROC' },
  { label: 'ROK', value: 'ROK' },
  { label: 'Singapore', value: 'Singapore' },
  { label: 'Turkey', value: 'Turkey' },
  // 'UCT'
  { label: 'US/Alaska', value: 'US/Alaska' },
  { label: 'US/Aleutian', value: 'US/Aleutian' },
  { label: 'US/Arizona', value: 'US/Arizona' },
  { label: 'US/Central', value: 'US/Central' },
  { label: 'US/East-Indiana', value: 'US/East-Indiana' },
  { label: 'US/Eastern', value: 'US/Eastern' },
  { label: 'US/Hawaii', value: 'US/Hawaii' },
  { label: 'US/Indiana-Starke', value: 'US/Indiana-Starke' },
  { label: 'US/Michigan', value: 'US/Michigan' },
  { label: 'US/Mountain', value: 'US/Mountain' },
  { label: 'US/Pacific', value: 'US/Pacific' },
  { label: 'US/Samoa', value: 'US/Samoa' },
  //'UTC'
  { label: 'Universal', value: 'Universal' },
  { label: 'W-SU', value: 'W-SU' },
  { label: 'WET', value: 'WET' },
  { label: 'Zulu', value: 'Zulu' },
];

export const COUNTRIES_SELECT_OPTIONS_USA_FIRST: OptionType[] =
  COUNTRIES.reduce(
    (result, country) => {
      if (country.value !== 'US') {
        result.push({ value: country.value, label: country.description });
      }
      return result;
    },
    [{ value: 'US', label: 'United States' }],
  );

export const PHONE_TYPE_OPTIONS = [
  { value: 'office', label: 'Office' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'other', label: 'Other' },
];

export const MASK_TO_PREFIX_MAP = {
  '128.0.0.0': '1',
  '192.0.0.0': '2',
  '224.0.0.0': '3',
  '240.0.0.0': '4',
  '248.0.0.0': '5',
  '252.0.0.0': '6',
  '254.0.0.0': '7',
  '255.0.0.0': '8',
  '255.128.0.0': '9',
  '255.192.0.0': '10',
  '255.224.0.0': '11',
  '255.240.0.0': '12',
  '255.248.0.0': '13',
  '255.252.0.0': '14',
  '255.254.0.0': '15',
  '255.255.0.0': '16',
  '255.255.128.0': '17',
  '255.255.192.0': '18',
  '255.255.224.0': '19',
  '255.255.240.0': '20',
  '255.255.248.0': '21',
  '255.255.252.0': '22',
  '255.255.254.0': '23',
  '255.255.255.0': '24',
  '255.255.255.128': '25',
  '255.255.255.192': '26',
  '255.255.255.224': '27',
  '255.255.255.240': '28',
  '255.255.255.248': '29',
  '255.255.255.252': '30',
  '255.255.255.254': '31',
  '255.255.255.255': '32',
};

export const PREFIX_TO_MASK_MAP = {
  '1': '128.0.0.0',
  '2': '192.0.0.0',
  '3': '224.0.0.0',
  '4': '240.0.0.0',
  '5': '248.0.0.0',
  '6': '252.0.0.0',
  '7': '254.0.0.0',
  '8': '255.0.0.0',
  '9': '255.128.0.0',
  '10': '255.192.0.0',
  '11': '255.224.0.0',
  '12': '255.240.0.0',
  '13': '255.248.0.0',
  '14': '255.252.0.0',
  '15': '255.254.0.0',
  '16': '255.255.0.0',
  '17': '255.255.128.0',
  '18': '255.255.192.0',
  '19': '255.255.224.0',
  '20': '255.255.240.0',
  '21': '255.255.248.0',
  '22': '255.255.252.0',
  '23': '255.255.254.0',
  '24': '255.255.255.0',
  '25': '255.255.255.128',
  '26': '255.255.255.192',
  '27': '255.255.255.224',
  '28': '255.255.255.240',
  '29': '255.255.255.248',
  '30': '255.255.255.252',
  '31': '255.255.255.254',
  '32': '255.255.255.255',
};

export const MSP_SUFFIX = (
  <span
    className="pl-1"
    style={{
      fontSize: '12px',
      display: 'inline-block',
      alignSelf: 'flex-end',
    }}
  >
    <b>MSP</b>
  </span>
);

export const formatOptionLabel = ({
  value,
  label,
  customElement,
}: OptionType) => (
  <div className="d-flex align-items-center">
    <span>{label}</span>
    {customElement}
  </div>
);

export const SENSOR_CONFIG_LABEL_MAP: Record<
  OBSensorConfigurations | OBSensorDeploymentCapabilities,
  string
> = {
  [OBSensorDeploymentCapabilities.MIRROR]: 'Port mirroring',
  [OBSensorDeploymentCapabilities.INTERNAL_TAP]: 'Internal tap',
  [OBSensorDeploymentCapabilities.MIRRORING]: 'Port mirroring',
  [OBSensorDeploymentCapabilities.INTERNALTAP]: 'Internal tap',
  [OBSensorConfigurations.SPAN_MIRROR]: 'Port mirroring',
  [OBSensorConfigurations.INTERNAL_TAP]: 'Internal tap',
};

export const NEWLINE_COMMA_DELIMITER = /[\n,]+/;

export enum OnboardingRequestEnum {
  CONTACTS = 'contacts',
  SITES = 'sites',
  SENSORS = 'sensors',
  SCANNERS = 'scanners',
  VIRTUAL = 'virtual scanners',
  ENDPOINT = 'endpoint configuration',
  VIRTUAL_SENSOR = 'virtual sensor configuration',
  AVAILABLE_ENDPOINT = 'available endpoint',
  AVAILABLE_VIRTUAL_SENSORS = 'available virtual sensors',
  CLOUD = 'cloud configuration',
  INTERNET_EXPOSURE = 'internet exposure',
  PRODUCTS = 'products',
  HARDWARE = 'available hardware',
  TEAM_CONTACTS = 'team contacts',
  COMPLETE_FORM = 'data entry completion',
  AVAILABLE_GENERIC_SENSORS = 'available generic sensors',
}

export const MA_SKUS = [
  OBServiceTypes.managedAwarenessPlus,
  OBServiceTypes.managedAwareness,
  OBServiceTypes.contentPack,
];

export const IRR_SKUS = [
  OBServiceTypes.irJumpstartRetainer,
  OBServiceTypes.jumpStartRetainer,
  OBServiceTypes.incident360,
  OBServiceTypes.incident360Plus,
];
export const LEGACY_CST_TIER_SKUS = [
  OBServiceTypes.legacyConciergeTier1,
  OBServiceTypes.legacyConciergeTier2,
  OBServiceTypes.legacyConciergeTier3,
];

export const StandardTooltipOverlay = styled.p`
  width: 300px;
  font-size: 14px;
  white-space: pre-wrap;
  padding: 10px;
`;

/**
 * Gets the list of available dropdown options for the supplied credential field
 * by checking if the credential datatype is the select type, and taking the options from the respective field.
 * @param field - credential field to get options for
 * @param name  - credential schema name
 * @returns array of options or null if not a dropdown field
 */
export const getDropdownOptions = (
  field: CredentialField,
  name: string,
): SelectOptions[] | null => {
  if (field.data_type === 'select') {
    if (field.options && field.options.length > 0) {
      return field.options
        .filter((value: string) => !(value === 'gcc' && name === 'azure_graph'))
        .map((value: string) => ({
          display: value,
          value,
        }));
    }
  }
  return null;
};

/**
 * Gets the list of subfields for the supplied fieldchoice credential field
 * by extracting them from the 'oneOf' validator, if present.
 * @param field - credential field to get subfields for
 * @returns array of subfields or null if not a fieldchoice field
 */
export const getSubfields = (field: CredentialField): string[] | null => {
  if (field.data_type === 'fieldchoice') {
    const oneOfValidator = field.validators?.find(
      (validator) => validator.validator_name === 'oneOf',
    );
    if (oneOfValidator && Array.isArray(oneOfValidator.validator_value)) {
      return oneOfValidator.validator_value as string[];
    }
  }
  return null;
};
